import React, { useEffect, useRef, useState } from 'react'
import { supabase } from './supabaseClient'
import { Search, X, Package, ShoppingCart, Users, FileText, Hash } from 'lucide-react'

import { C } from './theme'

const TYPE_META = {
  produto:    { icon: Package,      label: 'Produto',    color: C.primary,   page: 'catalogo' },
  encomenda:  { icon: ShoppingCart, label: 'Encomenda',  color: '#107c10',   page: 'financeiro' },
  perfil:     { icon: Users,        label: 'Cliente',    color: '#6264a7',   page: 'utilizadores' },
}

export function CommandPalette({ open, onClose, onNavigate }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [hoverIdx, setHoverIdx] = useState(0)
  const inputRef = useRef(null)
  const debounceRef = useRef(null)

  // Reset ao abrir
  useEffect(() => {
    if (open) {
      setQuery('')
      setResults([])
      setHoverIdx(0)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  // Search debounced
  useEffect(() => {
    if (!open) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const q = query.trim()
    if (q.length < 2) { setResults([]); return }
    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const [prodRes, encRes, perfRes] = await Promise.all([
          supabase.from('Produtos')
            .select('id, Nome, EAN, preco')
            .or(`Nome.ilike.%${q}%,EAN.ilike.%${q}%`)
            .limit(8),
          supabase.from('encomendas')
            .select('id, numero_encomenda, cliente_nome, cliente_email, total_final, total_com_iva, estado')
            .or(`numero_encomenda.ilike.%${q}%,cliente_nome.ilike.%${q}%,cliente_email.ilike.%${q}%`)
            .order('created_at', { ascending: false })
            .limit(8),
          supabase.from('perfis')
            .select('id, nome, email, role, ativo')
            .or(`nome.ilike.%${q}%,email.ilike.%${q}%`)
            .limit(8),
        ])
        const merged = []
        ;(prodRes.data || []).forEach(p => merged.push({
          type: 'produto', id: p.id,
          title: p.Nome,
          subtitle: [p.EAN ? `EAN ${p.EAN}` : null, p.preco ? `${Number(p.preco).toFixed(2)} €` : null].filter(Boolean).join(' · '),
          payload: p,
        }))
        ;(encRes.data || []).forEach(e => merged.push({
          type: 'encomenda', id: e.id,
          title: e.numero_encomenda || e.id.slice(0, 8).toUpperCase(),
          subtitle: [e.cliente_nome || e.cliente_email, `${Number(e.total_final ?? e.total_com_iva ?? 0).toFixed(2)} €`, e.estado].filter(Boolean).join(' · '),
          payload: e,
        }))
        ;(perfRes.data || []).forEach(p => merged.push({
          type: 'perfil', id: p.id,
          title: p.nome || p.email,
          subtitle: [p.email, p.role, p.ativo ? 'ativo' : 'inativo'].filter(Boolean).join(' · '),
          payload: p,
        }))
        setResults(merged)
        setHoverIdx(0)
      } finally {
        setLoading(false)
      }
    }, 200)
    return () => debounceRef.current && clearTimeout(debounceRef.current)
  }, [query, open])

  // Teclado: Esc fecha, ↑↓ navega, Enter selecciona
  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return }
      if (e.key === 'ArrowDown') { e.preventDefault(); setHoverIdx(i => Math.min(i + 1, results.length - 1)); return }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setHoverIdx(i => Math.max(i - 1, 0)); return }
      if (e.key === 'Enter')     { e.preventDefault(); const r = results[hoverIdx]; if (r) handleSelect(r); return }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, results, hoverIdx])

  const handleSelect = (r) => {
    const meta = TYPE_META[r.type]
    if (meta) onNavigate?.({ page: meta.page, type: r.type, item: r.payload })
    onClose()
  }

  if (!open) return null

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 800, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '10vh' }}
         onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           style={{ width: '100%', maxWidth: '600px', margin: '0 16px', background: C.surface, borderRadius: '8px', boxShadow: '0 16px 48px rgba(0,0,0,0.25)', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '70vh' }}>
        {/* Input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', borderBottom: `1px solid ${C.border}` }}>
          <Search size={16} color={C.textMuted} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Procurar produto, encomenda ou cliente…"
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: '15px', color: C.text, background: 'transparent' }}
          />
          <kbd style={{ fontSize: '11px', color: C.textMuted, padding: '2px 6px', background: C.bg, borderRadius: '3px', border: `1px solid ${C.border}` }}>Esc</kbd>
        </div>

        {/* Results */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {query.trim().length < 2 ? (
            <div style={{ padding: '36px 16px', textAlign: 'center', color: C.textMuted, fontSize: '13px' }}>
              <Hash size={28} style={{ opacity: 0.3, marginBottom: '8px' }} />
              <p style={{ margin: 0 }}>Escreve pelo menos 2 caracteres para procurar</p>
              <p style={{ margin: '6px 0 0', fontSize: '11px' }}>EAN · número de encomenda · nome de cliente · nome de produto</p>
            </div>
          ) : loading && results.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: C.textMuted, fontSize: '13px' }}>A procurar…</div>
          ) : results.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: C.textMuted, fontSize: '13px' }}>Nada encontrado para "{query}".</div>
          ) : (
            results.map((r, i) => {
              const meta = TYPE_META[r.type]
              const Icon = meta?.icon || FileText
              const active = i === hoverIdx
              return (
                <div
                  key={`${r.type}-${r.id}`}
                  onClick={() => handleSelect(r)}
                  onMouseEnter={() => setHoverIdx(i)}
                  style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', background: active ? '#eff6fc' : 'transparent', borderLeft: `3px solid ${active ? C.primary : 'transparent'}` }}
                >
                  <div style={{ width: '32px', height: '32px', borderRadius: '6px', background: meta?.color + '15', color: meta?.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={16} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.title}
                    </div>
                    {r.subtitle && (
                      <div style={{ fontSize: '11px', color: C.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.subtitle}
                      </div>
                    )}
                  </div>
                  <span style={{ fontSize: '10px', fontWeight: '700', color: meta?.color, textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0 }}>
                    {meta?.label}
                  </span>
                </div>
              )
            })
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '8px 16px', borderTop: `1px solid ${C.border}`, background: C.bg, display: 'flex', alignItems: 'center', gap: '14px', fontSize: '11px', color: C.textMuted }}>
          <span><kbd style={{ padding: '1px 5px', background: C.surface, borderRadius: '3px', border: `1px solid ${C.border}` }}>↑↓</kbd> navegar</span>
          <span><kbd style={{ padding: '1px 5px', background: C.surface, borderRadius: '3px', border: `1px solid ${C.border}` }}>↵</kbd> abrir</span>
          <span style={{ marginLeft: 'auto' }}>{results.length} resultado(s)</span>
        </div>
      </div>
    </div>
  )
}
